import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ObjectTypeInfo } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { FilterOption } from '@barthauer/abc/queries/opath/core';
import { EditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import { ParameterDefinition, ParameterType } from '@barthauer/abc/queries/parameters/core';
import { ListDataSource } from 'libs/abc/arena/src/lib/core/models/data.model';
import { HeaderButton } from 'libs/abc/queries/opath/core/src/lib/viewModels/layout.viewModel';
import { EditorInstanceSettings } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DefinitionsEditorService } from './definitions-editor.service';
import { Translation } from './models/definitions-editor.viewModel';

@Component({
    selector: 'parameters-definitions-editor',
    templateUrl: './definitions-editor.component.html',
    styleUrls: ['./definitions-editor.component.scss'],
    standalone: false
})
export class DefinitionsEditorComponent {
  @Output() onParameterSelected: EventEmitter<string> = new EventEmitter();
  @Output() onParameterRemoved: EventEmitter<string> = new EventEmitter();
  @Input() editorInstancesettings: EditorInstanceSettings;
  @Input() parameterDefinitions: ParameterDefinition[];

  public res: ResourceSet;
  public hedaerButtons: HeaderButton[] = [];
  public searchTerm: string = '';
  public currentFilterOption: FilterOption | undefined;
  public currentParameterDefinitions: ParameterDefinition[] = [];

  protected loading: boolean = true;
  protected translation: Translation;
  protected objectTypesInfo: ObjectTypeInfo[] = [];
  protected pickListByNameDataSource: ListDataSource;
  protected pickListByColumnDataSource: { Name: string; DisplayText: string }[];

  private unsubscribe$ = new Subject<void>();
  constructor(
    private resourcesService: ResourceManagerService,
    private definitionsEditorService: DefinitionsEditorService,
    private messageBoxService: MessageBoxService,
    private editorService: EditorService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.res = await this.resourcesService.getResourceSet('abc-parameters-definitions-editor');

    this.translation = {
      title: this.res.get('parameter-title'),
      add: this.res.get('parameter-add'),
      edit: this.res.get('parameter-edit'),
      delete: this.res.get('parameter-delete'),
      newParameter: this.res.get('parameter-new'),
      deleteQuestion: this.res.get('parameter-deleteQuestion'),
    };

    this.loadHeaderButtons();

    this.currentParameterDefinitions =
      this.definitionsEditorService.getParameterDefinitionsQueue(this.editorService.editorIndex) ?? [];

    this.objectTypesInfo = this.definitionsEditorService.getObjectTypesInfo();
    !this.pickListByNameDataSource && (await this.getPicklistByNameDataSource());
    !this.pickListByColumnDataSource && (await this.getPicklistByColumnDataSource());

    this.loading = false;
    this.loadHeaderButtons();

    // update the parameters list when a new expressions-editor instance delivers newly created parameters
    this.definitionsEditorService.onNewParameterDefinitions
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((newParameterDefinitions: ParameterDefinition[]) => {
        for (const newParameter of newParameterDefinitions) {
          const index = this.parameterDefinitions.findIndex((oldParameter) => oldParameter.Name === newParameter.Name);
          if (index !== -1) {
            this.parameterDefinitions[index] = newParameter;
          } else {
            this.parameterDefinitions.push(newParameter);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private async getPicklistByNameDataSource(): Promise<void> {
    this.pickListByNameDataSource = await this.definitionsEditorService.getPicklistByNameDataSource();
  }

  private async getPicklistByColumnDataSource(): Promise<void> {
    this.pickListByColumnDataSource = await this.definitionsEditorService.getPicklistByColumnDataSource();
  }

  public searchTermChanged(searchTerm: string): void {
    this.searchTerm = searchTerm;
  }

  public filterOptionChanged(filterOption: FilterOption): void {
    this.currentFilterOption = filterOption;
    if (this.searchTerm.length > 0) {
      this.searchTermChanged(this.searchTerm);
    }
  }

  public async loadHeaderButtons(): Promise<void> {
    this.hedaerButtons = [
      {
        icon: 'add',
        toolTipContent: this.translation.add,
        statfullButton: true,
        active: false,
        disabled: !!this.currentParameterDefinitions.length || this.loading,
        action: async () => this.addNewParameter(),
      },
    ];
  }

  public addNewParameter(): void {
    const parameter = new ParameterDefinition();
    parameter.Type = ParameterType.String;
    parameter.IsRequired = false;
    parameter.IsUserCreated = true;
    parameter.IsFixed = false;
    parameter.BeginGroup = false;
    parameter.AllowMultiSelect = false;

    this.currentParameterDefinitions.push(parameter);
    this.loadHeaderButtons();
  }

  public saveChanges = (): void => {
    if (!this.parameterDefinitions.includes(this.currentParameterDefinitions[0])) {
      this.parameterDefinitions.push(this.currentParameterDefinitions[0]);
    }

    this.cancelChanges();
  };

  public cancelChanges = (parameterDefinitionsBackup?: ParameterDefinition[]): void => {
    parameterDefinitionsBackup && (this.parameterDefinitions = parameterDefinitionsBackup);
    this.currentParameterDefinitions = [];
    this.definitionsEditorService.setParameterDefinitionsQueue(this.editorService.editorIndex, []);
    this.loadHeaderButtons();
  };

  public selectParameter(name: string): void {
    this.onParameterSelected.emit(name);
  }

  public deleteParameter(name: string): void {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.icon = MessageBoxIcon.Question;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = this.translation.deleteQuestion;

    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result: DialogResult) => {
        if (DialogResult.Yes === result['result']) {
          this.parameterDefinitions.splice(
            this.parameterDefinitions.findIndex((p) => p.Name === name),
            1
          );
          this.onParameterRemoved.emit(name);

          return;
        } else {
          return;
        }
      });
  }

  public editParameter(name: string): void {
    this.currentParameterDefinitions.push(this.parameterDefinitions.find((p) => p.Name === name));
    this.definitionsEditorService.setParameterDefinitionsQueue(
      this.editorService.editorIndex,
      this.currentParameterDefinitions
    );
    this.loadHeaderButtons();
  }
}
